/* PILSNERISH OS — pz-pixel-reveal "COLLABS" (owner B)
 *
 * One WebGL context for the whole section, kept off-DOM. Each frame it renders:
 *   1. every active card: trail pass (ping-pong FBO, vector = direction * strength, x0.88/frame decay,
 *      liquify swirl) + composite pass (7px block displacement up to ~31% of the card, image B through a
 *      13px-cell pixel blob with animated noise wobble), then blits that region into the card's own 2D canvas;
 *   2. the sky: domain-warped fbm clouds with per-column vertical offset/stretch streaks, blitted into the
 *      sticky background canvas at half resolution.
 * Because every output canvas lives inside its own DOM element, cards and labels never drift apart while
 * scrolling. The loop runs on gsap.ticker (after Lenis) and only while the section is on screen.
 * Degrades: no WebGL -> CSS sky + crossfade hover; reduced motion -> one static sky frame, static layout, crossfade.
 */
(function () {
  'use strict';
  var w = window, d = document;
  var PZ = w.PZ;
  if (!PZ || !PZ.onSection) return;

  /* ------------------------------------------------------------------ shaders */
  var VS = 'attribute vec2 aPos;varying vec2 vUv;void main(){vUv=aPos*.5+.5;gl_Position=vec4(aPos,0.,1.);}';
  var HEAD = '#ifdef GL_FRAGMENT_PRECISION_HIGH\nprecision highp float;\n#else\nprecision mediump float;\n#endif\nvarying vec2 vUv;\n';

  var HASH = [
    'float hash12(vec2 p){vec3 p3=fract(vec3(p.xyx)*.1031);p3+=dot(p3,p3.yzx+33.33);return fract((p3.x+p3.y)*p3.z);}',
    'float vnoise(vec2 p){vec2 i=floor(p),f=fract(p);vec2 u=f*f*(3.-2.*f);',
    'return mix(mix(hash12(i),hash12(i+vec2(1.,0.)),u.x),mix(hash12(i+vec2(0.,1.)),hash12(i+vec2(1.,1.)),u.x),u.y);}',
    'float fbm(vec2 p){float s=0.,a=.5;mat2 m=mat2(1.6,1.2,-1.2,1.6);for(int i=0;i<5;i++){s+=a*vnoise(p);p=m*p+vec2(3.1,1.7);a*=.5;}return s;}'
  ].join('\n');

  /* Ashima simplex 3D (MIT) */
  var SNOISE = [
    'vec4 perm(vec4 x){return mod(((x*34.)+1.)*x,289.);}',
    'float snoise(vec3 v){const vec2 C=vec2(1./6.,1./3.);const vec4 D=vec4(0.,.5,1.,2.);',
    'vec3 i=floor(v+dot(v,C.yyy));vec3 x0=v-i+dot(i,C.xxx);vec3 g=step(x0.yzx,x0.xyz);vec3 l=1.-g;',
    'vec3 i1=min(g.xyz,l.zxy);vec3 i2=max(g.xyz,l.zxy);vec3 x1=x0-i1+C.xxx;vec3 x2=x0-i2+C.yyy;vec3 x3=x0-D.yyy;',
    'i=mod(i,289.);vec4 p=perm(perm(perm(i.z+vec4(0.,i1.z,i2.z,1.))+i.y+vec4(0.,i1.y,i2.y,1.))+i.x+vec4(0.,i1.x,i2.x,1.));',
    'float n_=1./7.;vec3 ns=n_*D.wyz-D.xzx;vec4 j=p-49.*floor(p*ns.z*ns.z);vec4 x_=floor(j*ns.z);vec4 y_=floor(j-7.*x_);',
    'vec4 x=x_*ns.x+ns.yyyy;vec4 y=y_*ns.x+ns.yyyy;vec4 h=1.-abs(x)-abs(y);vec4 b0=vec4(x.xy,y.xy);vec4 b1=vec4(x.zw,y.zw);',
    'vec4 s0=floor(b0)*2.+1.;vec4 s1=floor(b1)*2.+1.;vec4 sh=-step(h,vec4(0.));',
    'vec4 a0=b0.xzyw+s0.xzyw*sh.xxyy;vec4 a1=b1.xzyw+s1.xzyw*sh.zzww;',
    'vec3 p0=vec3(a0.xy,h.x);vec3 p1=vec3(a0.zw,h.y);vec3 p2=vec3(a1.xy,h.z);vec3 p3=vec3(a1.zw,h.w);',
    'vec4 nr=1.79284291400159-.85373472095314*vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3));p0*=nr.x;p1*=nr.y;p2*=nr.z;p3*=nr.w;',
    'vec4 m=max(.6-vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)),0.);m=m*m;',
    'return 42.*dot(m*m,vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));}'
  ].join('\n');

  var FS_SKY = HEAD + HASH + [
    'uniform vec2 uRes;uniform float uTime,uScroll,uStreak,uCol;',
    'uniform vec3 cInk,cDeep,cGreen,cBone;',
    'float field(vec2 p){',
    ' vec2 q=vec2(fbm(p*.7+vec2(0.,uTime*.022)),fbm(p*.7+vec2(4.1,2.7)-uTime*.017));',
    ' return fbm(p+q*1.1+vec2(uTime*.012,0.));',
    '}',
    'void main(){',
    ' vec2 fc=gl_FragCoord.xy;',
    /* vertical pixel streaks: columns inside drifting bands get their own vertical offset + stretch */
    ' float col=floor(fc.x/uCol);',
    ' float h1=hash12(vec2(col,3.7)),h2=hash12(vec2(col,9.1)),h3=hash12(vec2(col,1.3));',
    ' float band=smoothstep(.57,.85,vnoise(vec2(col*.03+uTime*.035,7.3)))*(.5+.5*vnoise(vec2(col*.11,uTime*.25)));',
    ' float on=band*step(.4,h1)*uStreak;',
    ' vec2 uv=fc/uRes.y;',
    ' float anchor=.35+.4*h2;',
    ' uv.y=(uv.y-anchor)*mix(1.,.2+.45*h3,on)+anchor+(h3-.5)*.09*on;',
    ' vec2 p=uv*vec2(1.5,2.1)+vec2(uTime*.02,-uScroll*.9);',
    ' float f=field(p);',
    ' float fl=field(p+vec2(-.04,.06));',
    ' float a=smoothstep(.47,.74,f);',
    ' float lit=clamp((f-fl)*8.+.5,0.,1.);',
    ' float yN=fc.y/uRes.y;',
    ' vec3 sky=mix(cInk,cDeep,smoothstep(.05,1.,yN));',
    ' vec3 shade=mix(cDeep,cGreen,.25);',
    ' vec3 hi=mix(cGreen,cBone,.5);',
    ' vec3 cloud=mix(shade,hi,lit);',
    ' cloud=mix(cloud,cBone,smoothstep(.66,.9,f)*smoothstep(.45,1.,lit)*.55);',
    ' vec3 c=mix(sky,cloud,a*.94);',
    ' c+=on*.018*(h2-.3);',
    ' c*=.93+.07*step(.5,fract(fc.x*.5));',
    ' vec2 vv=fc/uRes-.5;c*=1.-dot(vv,vv)*.7;',
    ' c+=(hash12(fc+fract(uTime*7.)*91.)-.5)*(3./255.);',
    ' gl_FragColor=vec4(c,1.);',
    '}'
  ].join('\n');

  var FS_TRAIL = HEAD + [
    'uniform sampler2D uPrev;uniform vec2 uTexel,uM0,uM1;',
    'uniform float uAspect,uRadius,uPaint,uStrength,uTime,uDecay;',
    'vec2 dec(vec4 c){return (c.xy*255.-128.)/127.;}',
    'void main(){',
    ' vec2 st=vUv;float s=uTime*.9;',
    ' for(int i=0;i<5;i++){float fi=float(i);st+=.0028*vec2(cos(fi*9.*st.y+s),sin(fi*9.*st.x+s));}',
    ' st-=dec(texture2D(uPrev,vUv))*.018;',
    ' vec2 o=uTexel;',
    ' vec2 v=dec(texture2D(uPrev,st))*.36+(dec(texture2D(uPrev,st+vec2(o.x,0.)))+dec(texture2D(uPrev,st-vec2(o.x,0.)))+dec(texture2D(uPrev,st+vec2(0.,o.y)))+dec(texture2D(uPrev,st-vec2(0.,o.y))))*.16;',
    ' float mag=length(v);',
    ' v*=max(mag*uDecay-.006,0.)/max(mag,1e-5);',
    ' vec2 A=vec2(uM0.x*uAspect,uM0.y),B=vec2(uM1.x*uAspect,uM1.y),P=vec2(vUv.x*uAspect,vUv.y);',
    ' vec2 ab=B-A;float L=length(ab);',
    ' float h=clamp(dot(P-A,ab)/max(dot(ab,ab),1e-8),0.,1.);',
    ' float dd=length(P-A-ab*h);',
    ' float r=uRadius*mix(.5,1.,clamp(L*20.,0.,1.));',
    ' float br=(1.-smoothstep(r*.3,r,dd))*uPaint;',
    ' vec2 dir=L>1e-6?normalize(vec2(ab.x/uAspect,ab.y)):vec2(0.);',
    ' v=mix(v,dir*uStrength,br*.7)+dir*uStrength*br*.3;',
    ' float vl=length(v);if(vl>1.)v/=vl;',
    ' gl_FragColor=vec4((v*127.+128.)/255.,0.,1.);',
    '}'
  ].join('\n');

  var FS_COMP = HEAD + HASH + SNOISE + [
    'uniform sampler2D uA,uB,uTrail;',
    'uniform vec2 uScaleA,uScaleB,uSize,uBlob;',
    'uniform float uRadius,uTime,uHasB,uCell,uBlock,uVhs;',
    'uniform vec3 cInk,cRed,cBone;',
    'vec2 mir(vec2 u){return 1.-abs(1.-mod(u,2.));}',
    'vec3 img(sampler2D t,vec2 u,vec2 sc){u=mir(u);return texture2D(t,(vec2(u.x,1.-u.y)-.5)*sc+.5).rgb;}',
    'float b2(vec2 a){a=floor(a);return fract(dot(a,vec2(.5,a.y*.75)));}',
    'float bayer(vec2 a){return b2(.5*a)*.25+b2(a);}',
    'void main(){',
    ' vec2 px=vUv*uSize;',
    ' vec2 bc=(floor(px/uBlock)+.5)*uBlock;',
    ' vec2 v=(texture2D(uTrail,bc/uSize).xy*255.-128.)/127.;',
    ' vec2 disp=v*.78*.4;',
    /* VHS cut between drops: row tears, tracking bands, green-terminal ghosting (R4: phosphor green + red ghosts
       from the luminance of offset samples, never a raw R/B channel split, so no blue/cyan/magenta), green cast,
       scanlines, snow */
    ' float tf=floor(uTime*40.);',
    ' float tear=(hash12(vec2(floor(px.y/3.),tf))-.5)*.1;',
    ' float bnd=step(.74,hash12(vec2(floor(px.y/26.),floor(uTime*24.))));',
    ' vec2 vu=vUv+vec2((tear+bnd*(hash12(vec2(floor(px.y/26.),3.))-.5)*.5)*uVhs,0.);',
    ' vec2 uA2=vu-disp;',
    ' vec2 cs=vec2(uVhs*.03,0.);',
    ' vec3 a=img(uA,uA2,uScaleA);',
    ' float l0=dot(a,vec3(.299,.587,.114));',
    ' float lr=dot(img(uA,uA2+disp*.07+cs,uScaleA),vec3(.299,.587,.114));',
    ' float lg=dot(img(uA,uA2-cs,uScaleA),vec3(.299,.587,.114));',
    ' a+=vec3(.93,.11,.14)*max(lr-l0,0.)*.8+vec3(.2,1.,.4)*max(lg-l0,0.)*.9;',
    ' vec2 mpx=px-disp*uSize*.7;',
    ' vec2 org=uBlob*uSize*.56;',
    ' vec2 cell=(floor((mpx-org)/uCell)+.5)*uCell+org;',
    ' vec2 cn=cell/uSize.x;',
    ' float n1=snoise(vec3(cn*3.1,uTime*.62));',
    ' float n2=snoise(vec3(cn*3.1+7.7,uTime*.62+3.1));',
    ' vec2 wc=cell+vec2(n1,n2)*uSize.x*.075;',
    ' float dist=length(wc-uBlob*uSize)/uSize.x;',
    ' float m=step(dist,uRadius);',
    ' vec2 uB2=vu-disp*.7;',
    ' vec3 b;',
    ' if(uHasB>.5){b=img(uB,uB2,uScaleB);}',
    ' else{vec3 s=img(uA,uB2,uScaleA);float l=dot(s,vec3(.299,.587,.114));float th=bayer(floor(px/2.));',
    '  b=l*1.35>th+.55?cBone:(l*1.35>th*.9+.08?cRed:cInk);}',
    ' vec3 col=mix(a,b,m);',
    ' col=mix(col,col*vec3(.72,1.2,.8)+vec3(0.,.035,0.),uVhs*.6);',
    ' col*=1.-uVhs*.25*step(.5,fract(px.y*.5));',
    ' col+=(hash12(px+fract(uTime)*97.)-.5)*uVhs*.32;',
    ' gl_FragColor=vec4(col,1.);',
    '}'
  ].join('\n');

  function hex(h) { h = h.replace('#', ''); return [parseInt(h.slice(0, 2), 16) / 255, parseInt(h.slice(2, 4), 16) / 255, parseInt(h.slice(4, 6), 16) / 255]; }
  var C = { ink: hex('141513'), deep: hex('2C332F'), green: hex('4C6055'), bone: hex('EFECE6'), red: hex('ED1B24') };

  /* ------------------------------------------------------------------ engine */
  function Engine() {
    var cv = d.createElement('canvas');
    cv.width = 64; cv.height = 64;
    var o = { alpha: false, antialias: false, depth: false, stencil: false, premultipliedAlpha: false, preserveDrawingBuffer: false, powerPreference: 'high-performance' };
    var gl = null;
    try { gl = cv.getContext('webgl2', o) || cv.getContext('webgl', o) || cv.getContext('experimental-webgl', o); } catch (e) { gl = null; }
    if (!gl) return null;
    this.cv = cv; this.gl = gl; this.lost = false;
    var self = this;
    cv.addEventListener('webglcontextlost', function (e) { e.preventDefault(); self.lost = true; if (self.onlost) self.onlost(); });

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    /* R6 perf: compiling + linking the three programs took ~320 ms in one blocking task when their status was read
       right away. The work is only queued here; with KHR_parallel_shader_compile the driver compiles off the main
       thread and ready() polls without blocking; finish() reads the link status once it is done. */
    this.ext = gl.getExtension('KHR_parallel_shader_compile');
    this.pending = [this.begin(FS_SKY), this.begin(FS_TRAIL), this.begin(FS_COMP)];
    this.ok = false;
  }
  Engine.prototype.begin = function (fs) {
    var gl = this.gl;
    function sh(type, src) { var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); return s; }
    var v = sh(gl.VERTEX_SHADER, VS), f = sh(gl.FRAGMENT_SHADER, fs);
    var p = gl.createProgram();
    gl.attachShader(p, v); gl.attachShader(p, f);
    gl.bindAttribLocation(p, 0, 'aPos');
    gl.linkProgram(p);
    return { p: p, v: v, f: f };
  };
  Engine.prototype.ready = function () {
    if (!this.ext) return true;
    var gl = this.gl, k = this.ext.COMPLETION_STATUS_KHR;
    return this.pending.every(function (x) { return gl.getProgramParameter(x.p, k); });
  };
  Engine.prototype.finish = function () {
    var gl = this.gl, ok = true;
    var progs = this.pending.map(function (x) {
      if (!gl.getProgramParameter(x.p, gl.LINK_STATUS)) {
        ok = false;
        console.warn('[pz-pixel] link', gl.getProgramInfoLog(x.p), gl.getShaderInfoLog(x.v), gl.getShaderInfoLog(x.f));
      }
      return { p: x.p, u: {} };
    });
    this.pending = null;
    this.sky = progs[0]; this.trail = progs[1]; this.comp = progs[2];
    this.ok = ok;
    if (ok) this.dummy = this.texFromData();
    return ok;
  };
  Engine.prototype.use = function (pr) {
    var gl = this.gl;
    gl.useProgram(pr.p);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    this.cur = pr;
  };
  Engine.prototype.u = function (name) {
    var pr = this.cur;
    if (!(name in pr.u)) pr.u[name] = this.gl.getUniformLocation(pr.p, name);
    return pr.u[name];
  };
  Engine.prototype.f1 = function (n, a) { this.gl.uniform1f(this.u(n), a); };
  Engine.prototype.f2 = function (n, a, b) { this.gl.uniform2f(this.u(n), a, b); };
  Engine.prototype.f3 = function (n, a) { this.gl.uniform3f(this.u(n), a[0], a[1], a[2]); };
  Engine.prototype.i1 = function (n, a) { this.gl.uniform1i(this.u(n), a); };
  Engine.prototype.draw = function () { this.gl.drawArrays(this.gl.TRIANGLES, 0, 3); };
  Engine.prototype.ensure = function (wd, ht) {
    var cv = this.cv;
    if (cv.width < wd || cv.height < ht) {
      cv.width = Math.max(cv.width, Math.ceil(wd));
      cv.height = Math.max(cv.height, Math.ceil(ht));
    }
  };
  Engine.prototype.setTexParams = function () {
    var gl = this.gl;
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  };
  Engine.prototype.texFromData = function () {
    var gl = this.gl, t = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([44, 51, 47, 255]));
    this.setTexParams();
    return t;
  };
  Engine.prototype.texFromImg = function (img) {
    var gl = this.gl, t = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    try { gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img); }
    catch (e) { gl.deleteTexture(t); return null; }
    this.setTexParams();
    return t;
  };
  Engine.prototype.fbo = function (wd, ht) {
    var gl = this.gl, t = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, wd, ht, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    this.setTexParams();
    var f = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, f);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, t, 0);
    var fb = { t: t, f: f, w: wd, h: ht };
    this.clearFbo(fb);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return fb;
  };
  Engine.prototype.clearFbo = function (fb) {
    var gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb.f);
    gl.viewport(0, 0, fb.w, fb.h);
    gl.clearColor(128 / 255, 128 / 255, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
  };
  Engine.prototype.freeFbo = function (fb) {
    if (!fb) return;
    this.gl.deleteFramebuffer(fb.f); this.gl.deleteTexture(fb.t);
  };
  Engine.prototype.blit = function (ctx, wd, ht) {
    ctx.drawImage(this.cv, 0, this.cv.height - ht, wd, ht, 0, 0, wd, ht);
  };

  /* ------------------------------------------------------------------ card */
  var CELL = 13, BLOCK = 7, BLOB_R = 0.38, TAP_R = 1.75, CUT = 0.36;

  /* Liquid only exposes the top mp4 rendition, so ask the product JSON for 720p (desktop) / 480p (phones) */
  var prodCache = {};
  function productJson(handle) {
    if (!prodCache[handle]) {
      var root = (w.Shopify && w.Shopify.routes && w.Shopify.routes.root) || '/';
      prodCache[handle] = fetch(root + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return prodCache[handle];
  }
  function pickSource(prod, mediaId, want) {
    if (!prod || !prod.media) return null;
    var m = null;
    prod.media.forEach(function (x) { if (!m && x.media_type === 'video' && String(x.id) === String(mediaId)) m = x; });
    if (!m) prod.media.forEach(function (x) { if (!m && x.media_type === 'video') m = x; });
    if (!m || !m.sources) return null;
    var mp4 = m.sources.filter(function (x) { return x.format === 'mp4' && x.url; });
    mp4.sort(function (a, b) { return Math.abs(a.height - want) - Math.abs(b.height - want); });
    return mp4.length ? shopVideoUrl(mp4[0].url) : null;
  }
  /* R2: the product JSON lists cdn.shopify.com video urls, which answer the browser's first "Range: bytes=0-"
     request with a plain 200 (no ranges) -> the video is not seekable and intro skips / loops can't seek.
     The same file through the shop domain (/cdn/shop/videos/...) is same-origin and always answers 206. */
  function shopVideoUrl(u) { return String(u || '').replace(/^(https?:)?\/\/cdn\.shopify\.com\/videos\//, '/cdn/shop/videos/'); }

  function imgReady(im) { return !!(im && im.tagName === 'IMG' && im.complete && im.naturalWidth); }
  function vidReady(v) { return !!(v && v.readyState >= 2 && v.videoWidth); }

  /* One collaborator card. Owns its drops (one per product), cycling, the VHS cut and video playback.
     The WebGL part (hover reveal, cut) only runs when an engine exists. */
  function Card(el, eng, idx, opts) {
    this.el = el; this.eng = eng; this.idx = idx; this.opts = opts;
    this.media = el.querySelector('.pzpx__media');
    this.collab = el.querySelector('.pzpx__collab');
    this.links = [].slice.call(el.querySelectorAll('[data-pzpx-link]'));
    this.rows = [].slice.call(el.querySelectorAll('.pzpx__row'));
    this.counter = el.querySelector('.pzpx__count');
    this.drops = [].slice.call(el.querySelectorAll('.pzpx__drop')).map(function (dr) {
      var a = dr.querySelector('.pzpx__sa');
      return {
        el: dr, a: a, b: dr.querySelector('.pzpx__sb'),
        video: a && a.tagName === 'VIDEO' ? a : null,
        url: dr.getAttribute('data-url'), name: dr.getAttribute('data-name') || '',
        peek: dr.getAttribute('data-peek') || '',
        tA: null, tB: null, tV: null
      };
    });
    /* peek k shows the drop k places after the front one; kept in step by setDrop */
    this.peeks = [].slice.call(el.querySelectorAll('.pzpx__peek')).map(function (p) {
      return { el: p, k: parseInt(p.getAttribute('data-peek'), 10) || 1, img: p.querySelector('img') };
    });
    this.cur = 0;
    this.hydrated = false;
    this.fb = null; this.cv = null; this.ctx = null;
    this.active = false; this.shown = false;
    this.reveal = 0; this.revealT = 0; this.R = BLOB_R;
    this.m = [0.5, 0.5]; this.m0 = [0.5, 0.5]; this.blob = [0.5, 0.5];
    this.inside = false; this.still = 0; this.burst = 0; this.tapped = false;
    this.cut = null; this.visible = false; this.primed = false; this.nextAt = 0;
    if (this.peeks.length) this.syncPeeks(0, false);
    this.el.classList.toggle('is-plain', this.plain());
  }
  Card.prototype.multi = function () { return this.drops.length > 1; };
  /* R3: a drop without a B side (product video, animated GIF, single image) is "plain": hover never starts the
     GL reveal on it (the GL texture of a GIF is one frozen frame, and the reveal hid the moving video), it keeps
     playing and only gets the CSS glitch outline (.pzpx__card.is-plain .pzpx__gx) */
  Card.prototype.plain = function () { var dr = this.drops[this.cur]; return !dr || !dr.b; };
  /* R2: peek k = the drop k places after drop i (its first still). A peek never repeats the front drop or an
     image the front card can show (A side, poster, B side); if it would, it hides. Each peek is a button
     that jumps the deck to its drop (wired in init). */
  function filePath(u) { return String(u || '').replace(/^(https?:)?\/\/[^\/]+/, '').split('?')[0]; }
  Card.prototype.syncPeeks = function (i, cut) {
    var n = this.drops.length, front = this.drops[i], motion = this.opts.motion;
    if (!front) return;
    var shown = [front.peek];
    [front.a, front.b].forEach(function (m) {
      if (!m) return;
      shown.push(m.currentSrc || m.getAttribute('src') || m.getAttribute('data-src') || '');
      if (m.tagName === 'VIDEO') shown.push(m.getAttribute('poster') || '');
    });
    shown = shown.map(filePath).filter(Boolean);
    var drops = this.drops, collab = this.collab ? this.collab.textContent.trim() : '', hyd = this.hydrated;
    this.peeks.forEach(function (p) {
      var j = (i + p.k) % n, dr = drops[j];
      var ok = n > p.k && j !== i && dr && dr.peek && shown.indexOf(filePath(dr.peek)) < 0;
      p.el.hidden = !ok;
      p.to = ok ? j : -1;
      if (!ok) return;
      if (p.img && filePath(p.img.getAttribute('src') || p.img.getAttribute('data-src')) !== filePath(dr.peek)) {
        p.img.removeAttribute('srcset');
        p.img.setAttribute(hyd ? 'src' : 'data-src', dr.peek);   /* R4 perf: deferred until the card is near */
      }
      p.el.setAttribute('aria-label', 'show ' + (collab ? collab + ' ' : '') + 'drop ' + ('0' + (j + 1)).slice(-2) + ': ' + dr.name);
      if (cut && motion) {
        p.el.classList.remove('is-cut'); void p.el.offsetWidth; p.el.classList.add('is-cut');
        clearTimeout(p._t);
        p._t = setTimeout(function () { p.el.classList.remove('is-cut'); }, CUT * 1000 + 40);
      }
    });
  };
  /* R4 perf: the card's images, peeks and video posters are deferred in the markup (data-src / data-srcset /
     data-poster) and only get real attributes here, once the card is within ~1 viewport (reduced motion too) */
  Card.prototype.hydrate = function () {
    if (this.hydrated) return;
    this.hydrated = true;
    [].forEach.call(this.el.querySelectorAll('img[data-srcset], source[data-srcset]'), function (n) { n.setAttribute('srcset', n.getAttribute('data-srcset')); n.removeAttribute('data-srcset'); });
    [].forEach.call(this.el.querySelectorAll('img[data-src]'), function (n) { n.setAttribute('src', n.getAttribute('data-src')); n.removeAttribute('data-src'); });
    [].forEach.call(this.el.querySelectorAll('video[data-poster]'), function (n) { n.setAttribute('poster', n.getAttribute('data-poster')); n.removeAttribute('data-poster'); });
  };
  /* start loading everything this card can show (images eager, videos get a source) */
  Card.prototype.prime = function () {
    if (this.primed) return;
    this.primed = true;
    this.hydrate();
    var self = this;
    var desk = w.matchMedia('(min-width: 750px)').matches;
    this.drops.forEach(function (dr) {
      [dr.a, dr.b].forEach(function (im) { if (im && im.tagName === 'IMG' && im.loading === 'lazy') im.loading = 'eager'; });
      if (dr.peek && self.peeks.length) { var pim = new Image(); pim.decoding = 'async'; pim.src = dr.peek; }
      var v = dr.video;
      if (v && !v.getAttribute('src')) {
        var fallback = v.getAttribute('data-src');
        var set = function (url) {
          if (v.getAttribute('src') || !url) return;
          v.preload = 'auto'; v.src = url;
          try { v.load(); } catch (e) {}
          self.syncVideo();
        };
        productJson(v.getAttribute('data-handle')).then(function (prod) {
          set(pickSource(prod, v.getAttribute('data-media'), desk ? 720 : 480) || fallback);
        });
      }
    });
  };
  Card.prototype.syncVideo = function () {
    var self = this;
    this.drops.forEach(function (dr, i) {
      var v = dr.video;
      if (!v) return;
      if (i === self.cur && self.visible && v.getAttribute('src')) {   /* R9: under reduced motion too (muted product video) */
        var pr = v.play(); if (pr && pr.catch) pr.catch(function () {});
      } else if (!v.paused) v.pause();
    });
  };
  /* swap the DOM to drop i (media, label row, links, counter, x-ray tag, video) */
  Card.prototype.setDrop = function (i) {
    var self = this;
    this.cur = i;
    var dr = this.drops[i];
    this.drops.forEach(function (x, k) { x.el.classList.toggle('is-on', k === i); });
    this.rows.forEach(function (r) { r.hidden = String(r.getAttribute('data-drop')) !== String(i); });
    this.links.forEach(function (a) { a.setAttribute('href', dr.url); });
    if (this.media && this.collab) this.media.setAttribute('aria-label', this.collab.textContent.trim() + ': ' + dr.name);
    if (this.counter) this.counter.textContent = ('0' + (i + 1)).slice(-2) + '/' + ('0' + this.drops.length).slice(-2);
    this.el.classList.toggle('is-plain', !dr.b);
    if (!dr.b) { this.inside = false; if (!this.tapped) this.revealT = 0; }
    if (this.peeks.length) this.syncPeeks(i, true);
    this.syncVideo();
    var row = this.rows[i];
    var nm = row && row.querySelector('.pzpx__name');
    if (nm && this.opts.motion) PZ.scramble(nm, { accent: 'var(--green)' });
    this.el.dispatchEvent(new CustomEvent('pzpx:drop', { bubbles: true, detail: { index: i, url: dr.url } }));
    void self;
  };
  /* next / previous drop with a VHS cut: in WebGL when possible, else a CSS cut (or instant under reduced motion) */
  Card.prototype.go = function (dir) {
    if (!this.multi() || this.cut) return;
    var n = this.drops.length, to = (this.cur + dir + n) % n;
    this.prime();
    if (this.opts.gl && this.eng && !this.eng.lost) {
      this.cut = { t: 0, to: to, swapped: false };
      if (!this.inside && !this.tapped && this.revealT === 0) this.still = 999;
      this.active = true;
      return;
    }
    this.setDrop(to);
    if (this.opts.motion) {
      var m = this.media;
      m.classList.remove('is-cut'); void m.offsetWidth; m.classList.add('is-cut');
      clearTimeout(this._ct);
      this._ct = setTimeout(function () { m.classList.remove('is-cut'); }, CUT * 1000 + 40);
    }
  };
  /* GL sources for a drop: A = video frame (or image), B = the still behind it */
  Card.prototype.src = function (dr) {
    var eng = this.eng, gl = eng.gl, out = { A: null, aw: 1, ah: 1, B: null, bw: 1, bh: 1 };
    if (dr.video && vidReady(dr.video) && !(PZ.videoInIntro && PZ.videoInIntro(dr.video))) {
      if (!dr.tV) { dr.tV = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, dr.tV); eng.setTexParams(); }
      gl.bindTexture(gl.TEXTURE_2D, dr.tV);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      try { gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, dr.video); out.A = dr.tV; out.aw = dr.video.videoWidth; out.ah = dr.video.videoHeight; }
      catch (e) { dr.video = null; }
    }
    if (!out.A && dr.a && imgReady(dr.a)) {
      if (!dr.tA) dr.tA = eng.texFromImg(dr.a);
      if (dr.tA) { out.A = dr.tA; out.aw = dr.a.naturalWidth; out.ah = dr.a.naturalHeight; }
    }
    if (dr.b && imgReady(dr.b)) {
      if (!dr.tB) dr.tB = eng.texFromImg(dr.b);
      if (dr.tB) { out.B = dr.tB; out.bw = dr.b.naturalWidth; out.bh = dr.b.naturalHeight; }
    }
    /* video not decoded yet: stand in with the still so the card never flashes empty */
    if (!out.A && out.B) { out.A = out.B; out.aw = out.bw; out.ah = out.bh; }
    return out;
  };
  Card.prototype.size = function () {
    var cw = this.media.offsetWidth, ch = this.media.offsetHeight;
    /* R8 CPU: card canvases at most 1.5x (1x on phones) */
    var pr = Math.min(w.devicePixelRatio || 1, w.matchMedia('(max-width: 749px)').matches ? 1 : 1.5);
    return { cw: cw, ch: ch, pw: Math.max(2, Math.round(cw * pr)), ph: Math.max(2, Math.round(ch * pr)) };
  };
  Card.prototype.local = function (e) {
    var r = this.media.getBoundingClientRect();
    return [(e.clientX - r.left) / Math.max(1, r.width), 1 - (e.clientY - r.top) / Math.max(1, r.height)];
  };
  function coverOf(cw, ch, iw, ih) {
    var ca = cw / Math.max(1, ch), ia = iw / Math.max(1, ih);
    return ia > ca ? [ca / ia, 1] : [1, ia / ca];
  }
  Card.prototype.render = function (dt, t) {
    var eng = this.eng, gl = eng.gl;
    var vhs = 0;
    if (this.cut) {
      this.cut.t += dt / CUT;
      if (!this.cut.swapped && this.cut.t >= 0.5) { this.cut.swapped = true; this.setDrop(this.cut.to); }
      vhs = Math.sin(Math.min(1, this.cut.t) * Math.PI);
      if (this.cut.t >= 1) { this.cut = null; vhs = 0; }
    }
    var S = this.src(this.drops[this.cur]);
    if (!S.A) { if (!this.cut && !this.inside && !this.tapped) this.sleep(); return; }
    var s = this.size();
    if (!this.cv) {
      this.cv = d.createElement('canvas');
      this.cv.className = 'pzpx__cv';
      this.cv.setAttribute('aria-hidden', 'true');
      this.media.appendChild(this.cv);
      this.ctx = this.cv.getContext('2d', { alpha: false });
    }
    if (this.cv.width !== s.pw || this.cv.height !== s.ph) { this.cv.width = s.pw; this.cv.height = s.ph; }
    var tw = Math.max(8, Math.ceil(s.cw / 3)), th = Math.max(8, Math.ceil(s.ch / 3));
    if (!this.fb || this.fb[0].w !== tw || this.fb[0].h !== th) {
      if (this.fb) { eng.freeFbo(this.fb[0]); eng.freeFbo(this.fb[1]); }
      this.fb = [eng.fbo(tw, th), eng.fbo(tw, th)];
    }
    eng.ensure(s.pw, s.ph);

    /* scripted swirl for taps (touch has no hover trail) */
    if (this.burst > 0) {
      var a = (1 - this.burst) * Math.PI * 3.2, rr = 0.1 + 0.08 * (1 - this.burst);
      this.m = [this.tap[0] + Math.cos(a) * rr * (s.ch / s.cw), this.tap[1] + Math.sin(a) * rr];
      this.burst = Math.max(0, this.burst - dt * 1.6);
    }

    var k = 1 - Math.exp(-dt * 7);
    this.reveal += (this.revealT - this.reveal) * k;
    var kb = 1 - Math.exp(-dt * 10);
    var target = this.burst > 0 ? this.tap : this.m;
    this.blob[0] += (target[0] - this.blob[0]) * kb;
    this.blob[1] += (target[1] - this.blob[1]) * kb;

    var dx = (this.m[0] - this.m0[0]) * s.cw, dy = (this.m[1] - this.m0[1]) * s.ch;
    var dist = Math.sqrt(dx * dx + dy * dy);
    var painting = (this.inside || this.burst > 0) && dist > 0.25;
    if (painting) this.still = 0; else this.still++;

    /* pass 1: trail */
    var src = this.fb[0], dst = this.fb[1];
    gl.bindFramebuffer(gl.FRAMEBUFFER, dst.f);
    gl.viewport(0, 0, dst.w, dst.h);
    eng.use(eng.trail);
    gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, src.t);
    eng.i1('uPrev', 0);
    eng.f2('uTexel', 1 / dst.w, 1 / dst.h);
    eng.f2('uM0', this.m0[0], this.m0[1]);
    eng.f2('uM1', this.m[0], this.m[1]);
    eng.f1('uAspect', s.cw / s.ch);
    eng.f1('uRadius', 0.15 * s.cw / s.ch);
    eng.f1('uPaint', painting ? 1 : 0);
    eng.f1('uStrength', Math.min(1, 0.5 + dist / 22));
    eng.f1('uTime', t);
    eng.f1('uDecay', Math.pow(0.88, dt * 60));
    eng.draw();
    this.fb = [dst, src];
    this.m0 = [this.m[0], this.m[1]];

    /* pass 2: composite into the shared canvas, then blit */
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, s.pw, s.ph);
    eng.use(eng.comp);
    gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, S.A);
    gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, S.B || eng.dummy);
    gl.activeTexture(gl.TEXTURE2); gl.bindTexture(gl.TEXTURE_2D, dst.t);
    eng.i1('uA', 0); eng.i1('uB', 1); eng.i1('uTrail', 2);
    var ca = coverOf(s.cw, s.ch, S.aw, S.ah), cb = coverOf(s.cw, s.ch, S.bw, S.bh);
    eng.f2('uScaleA', ca[0], ca[1]);
    eng.f2('uScaleB', cb[0], cb[1]);
    eng.f2('uSize', s.cw, s.ch);
    eng.f2('uBlob', this.blob[0], this.blob[1]);
    eng.f1('uRadius', this.reveal * this.R);
    eng.f1('uTime', t);
    eng.f1('uHasB', S.B ? 1 : 0);
    eng.f1('uCell', CELL);
    eng.f1('uBlock', BLOCK);
    eng.f1('uVhs', vhs);
    eng.f3('cInk', C.ink); eng.f3('cRed', C.red); eng.f3('cBone', C.bone);
    eng.draw();
    eng.blit(this.ctx, s.pw, s.ph);
    if (!this.shown) { this.shown = true; this.cv.classList.add('is-on'); }
    this.el.classList.toggle('is-revealed', this.reveal > 0.5 && this.revealT > 0);

    /* go idle once the blob is closed, the trail has decayed and no cut is running */
    if (!this.cut && !this.inside && !this.tapped && this.revealT === 0 && this.reveal < 0.004 && this.still > 80) this.sleep();
  };
  Card.prototype.sleep = function () {
    this.active = false;
    this.shown = false;
    this.reveal = 0;
    if (this.cv) this.cv.classList.remove('is-on');
    this.el.classList.remove('is-revealed');
    if (this.fb) { this.eng.clearFbo(this.fb[0]); this.eng.clearFbo(this.fb[1]); this.eng.gl.bindFramebuffer(this.eng.gl.FRAMEBUFFER, null); }
  };
  Card.prototype.destroy = function () {
    var eng = this.eng;
    clearTimeout(this._ct);
    this.drops.forEach(function (dr) {
      if (dr.video && !dr.video.paused) dr.video.pause();
      if (eng && eng.gl) ['tA', 'tB', 'tV'].forEach(function (k) { if (dr[k]) eng.gl.deleteTexture(dr[k]); });
    });
    if (eng && this.fb) { eng.freeFbo(this.fb[0]); eng.freeFbo(this.fb[1]); }
    if (this.cv && this.cv.parentNode) this.cv.parentNode.removeChild(this.cv);
  };

  /* fit a display label so its longest word never breaks */
  function fitWords(el) {
    el.style.fontSize = '';
    var max = parseFloat(getComputedStyle(el).fontSize) || 32;
    var size = max;
    for (var k = 0; k < 4 && el.scrollWidth > el.clientWidth + 1 && el.clientWidth > 0; k++) {
      size = Math.max(12, Math.floor(size * el.clientWidth / el.scrollWidth));
      el.style.fontSize = size + 'px';
    }
  }

  /* ------------------------------------------------------------------ section */
  function init(root) {
    var gsap = w.gsap, ST = w.ScrollTrigger;
    var reduced = PZ.reduced;
    var canHover = w.matchMedia('(hover: hover) and (pointer: fine)').matches;
    var track = root.querySelector('.pzpx__track');
    var stage = root.querySelector('.pzpx__stage');
    var field = root.querySelector('.pzpx__field');
    var title = root.querySelector('.pzpx__title');
    var head = root.querySelector('.pzpx__head');
    var bgcv = root.querySelector('.pzpx__bgcv');
    var slots = [].slice.call(root.querySelectorAll('.pzpx__slot'));
    var cardEls = [].slice.call(root.querySelectorAll('[data-pzpx-card]'));
    var offs = [];
    function on(t, ev, fn, o) { t.addEventListener(ev, fn, o); offs.push(function () { t.removeEventListener(ev, fn, o); }); }
    var names = [].slice.call(root.querySelectorAll('.pzpx__collab'));
    function fitAll() { names.forEach(fitWords); }
    fitAll();
    if (d.fonts && d.fonts.ready) d.fonts.ready.then(fitAll);
    var ft;
    on(w, 'resize', function () { clearTimeout(ft); ft = setTimeout(fitAll, 120); });

    var speed = parseFloat(root.getAttribute('data-speed'));
    if (isNaN(speed)) speed = 1;
    var streak = parseFloat(root.getAttribute('data-streak'));
    if (isNaN(streak)) streak = 0.6;
    var cycleMs = (parseFloat(root.getAttribute('data-cycle')) || 3) * 1000;

    /* ---- engine ----
       R6 perf: no WebGL at boot. Until the engine is ready (built when the section is ~1 viewport away, shaders
       compiled in parallel) the cards use the CSS mode: plain crossfade hover, CSS sky gradient. */
    var eng = null, dead = false;
    var useGLHover = false;
    var opts = { gl: false, motion: !reduced };
    var cards = cardEls.map(function (el, i) { return new Card(el, null, i, opts); });
    var bgctx = null;

    var running = false, visible = false, t = 0, last = 0, clock = 0;
    var bgScale = 0.5, slowFrames = 0;
    function measure() {
      var vw = bgcv.clientWidth || w.innerWidth;
      var vh = bgcv.clientHeight || w.innerHeight;
      var bw = Math.max(2, Math.round(vw * bgScale)), bh = Math.max(2, Math.round(vh * bgScale));
      if (bgcv.width !== bw || bgcv.height !== bh) { bgcv.width = bw; bgcv.height = bh; }
    }
    function scrollProgress() {
      var r = root.getBoundingClientRect();
      return (w.innerHeight - r.top) / (r.height + w.innerHeight);
    }
    function renderSky() {
      if (!eng || eng.lost) return;
      var gl = eng.gl, bw = bgcv.width, bh = bgcv.height;
      eng.ensure(bw, bh);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, bw, bh);
      eng.use(eng.sky);
      eng.f2('uRes', bw, bh);
      eng.f1('uTime', t);
      eng.f1('uScroll', scrollProgress());
      eng.f1('uStreak', streak);
      eng.f1('uCol', 2);
      eng.f3('cInk', C.ink); eng.f3('cDeep', C.deep); eng.f3('cGreen', C.green); eng.f3('cBone', C.bone);
      eng.draw();
      eng.blit(bgctx, bw, bh);
      bgcv.classList.add('is-on');
    }
    /* R8 CPU: the GSAP ticker fires at the display rate (144-360 Hz on some screens); the cloud sky only needs ~30 fps
       and a card under the pointer or mid-cut ~60 fps, so other ticks return straight away */
    function frame() {
      if (!running || eng.lost) return;
      var now = performance.now();
      var hot = false;
      for (var h = 0; h < cards.length; h++) if (cards[h].active) { hot = true; break; }
      if (last && now - last < (hot ? 12 : 24)) return;
      var dt = last ? Math.min(0.05, (now - last) / 1000) : 1 / 60;
      last = now;
      t += dt * speed;
      clock += dt;
      /* adaptive: if the sky alone is too slow, drop its resolution once */
      if (dt > 0.03) slowFrames++; else slowFrames = Math.max(0, slowFrames - 1);
      if (slowFrames > 45 && bgScale > 0.34) { bgScale = 0.34; slowFrames = 0; measure(); }
      for (var i = 0; i < cards.length; i++) if (cards[i].active) cards[i].render(dt, clock);
      renderSky();
    }
    /* R8 CPU: own paced loop instead of the GSAP ticker (which fires at the display rate): after each frame it waits
       with a timer and asks for exactly one animation frame, ~30 per second for the sky, ~60 while a card is hot */
    function start() {
      if (running || !eng) return;
      running = true; last = 0;
      loopRaf();
    }
    var rafId = 0, padT = 0;
    function loopRaf() {
      rafId = 0;
      if (!running) return;
      var hot = false;
      for (var h = 0; h < cards.length; h++) if (cards[h].active) { hot = true; break; }
      /* R8 CPU: the page is idle (45 s without input) and no card is busy: the sky holds its last frame */
      if (PZ.idle && !hot) { padT = 0; return; }
      frame();
      padT = setTimeout(function () { if (running) rafId = requestAnimationFrame(loopRaf); }, hot ? 8 : 25);
    }
    function wakeLoop() { if (running && !rafId) { clearTimeout(padT); padT = 0; rafId = requestAnimationFrame(loopRaf); } }
    on(d, 'pz:idle', function (e) { if (!e.detail) wakeLoop(); });
    function stop() {
      if (!running) return;
      running = false;
      clearTimeout(padT); padT = 0;
      cancelAnimationFrame(rafId); rafId = 0;
    }

    function onEngine(e) {
      if (dead) return;
      eng = e;
      cards.forEach(function (c) { c.eng = e; });
      useGLHover = !reduced;
      opts.gl = useGLHover;
      root.classList.add('is-gl');
      bgctx = bgcv.getContext('2d', { alpha: false });
      eng.onlost = function () {
        stop(); opts.gl = false; useGLHover = false;
        root.classList.remove('is-gl');
        cards.forEach(function (c) { c.cut = null; c.active = false; if (c.cv) c.cv.classList.remove('is-on'); });
      };
      measure();
      if (reduced) {
        /* one still frame of sky; hover falls back to the CSS crossfade */
        root.classList.remove('is-gl');
        root.classList.add('is-sky');
        t = 24;
        offs.push(PZ.inView(root, function (inside) { if (inside) { measure(); renderSky(); } }, { rootMargin: '200px' }));
        bgcv.style.opacity = '1';
      } else {
        offs.push(PZ.inView(root, function (inside) { visible = inside; if (inside) start(); else stop(); }, { rootMargin: '100px' }));
        var rs;
        on(w, 'resize', function () { clearTimeout(rs); rs = setTimeout(measure, 100); });
        on(d, 'visibilitychange', function () { if (d.hidden) stop(); else if (visible) start(); });
      }
    }
    var engTried = false;
    function buildEngine() {
      if (engTried || dead) return;
      engTried = true;
      var e = null;
      try { e = new Engine(); if (!e.gl) e = null; } catch (err) { e = null; }
      if (!e) return;
      var tries = 0;
      (function wait() {
        if (dead) return;
        /* poll the parallel compile once a frame (~4 s cap, then read the status anyway) */
        if (!e.ready() && tries++ < 240) { requestAnimationFrame(wait); return; }
        if (e.finish()) onEngine(e);
      })();
    }
    offs.push(PZ.inView(root, function (inside) { if (inside) buildEngine(); }, { once: true, rootMargin: '100% 0px', media: false }));

    var kicker = root.querySelector('.pzpx__kicker');
    if (kicker) offs.push(PZ.inView(root, function (inside) { if (inside) PZ.scramble(kicker, { accent: 'var(--red)' }); }, { once: true, threshold: 0.05 }));

    /* ---- per card: prime media near the viewport, play the active video only while visible ---- */
    cards.forEach(function (c) {
      offs.push(PZ.inView(c.el, function (inside) { if (inside) c.hydrate(); }, { once: true, rootMargin: '100% 0px', media: false }));
      offs.push(PZ.inView(c.el, function (inside) {
        c.visible = inside;
        if (inside) c.prime();
        c.syncVideo();
      }, { rootMargin: '300px' }));
    });

    /* ---- multi-drop cycling ---- */
    var multi = cards.filter(function (c) { return c.multi(); });
    /* R9: decks keep cycling under reduced motion, as instant swaps (no VHS cut, no scramble: opts.motion) */
    if (multi.length) {
      var t0 = performance.now();
      multi.forEach(function (c, k) { c.nextAt = t0 + cycleMs + k * 520; });
      var cyc = setInterval(function () {
        if (d.hidden || PZ.isOff && PZ.isOff(root)) return;
        var now = performance.now();
        multi.forEach(function (c) {
          if (!c.visible || c.inside || c.tapped || c.cut || c.hold) return;
          if (now >= c.nextAt) { c.nextAt = now + cycleMs; c.go(1); }
        });
      }, 200);
      offs.push(function () { clearInterval(cyc); });
    }

    /* ---- hover / tap / controls ---- */
    function closeTapped(except) {
      cards.forEach(function (c) {
        if (c.el === except) return;
        c.el.classList.remove('is-tapped');
        if (c.tapped) { c.tapped = false; c.revealT = 0; c.nextAt = performance.now() + cycleMs; }
      });
    }
    cards.forEach(function (c) {
      var el = c.el, media = c.media;
      var lastType = 'mouse';
      on(el, 'pointerdown', function (e) { lastType = e.pointerType || 'mouse'; });
      [].forEach.call(el.querySelectorAll('.pzpx__ctrl button'), function (btn) {
        on(btn, 'click', function (e) {
          e.preventDefault();
          c.go(parseInt(btn.getAttribute('data-dir'), 10) || 1);
          c.nextAt = performance.now() + cycleMs * 2;
        });
      });
      /* a peek card jumps the deck to the drop it shows, same cut + timer as [>] (buttons: Enter/Space too) */
      c.peeks.forEach(function (p) {
        on(p.el, 'click', function (e) {
          e.preventDefault();
          if (p.to < 0 || p.to === c.cur) return;
          c.go(p.k);
          c.nextAt = performance.now() + cycleMs * 2;
        });
      });
      on(el, 'pointerenter', function (e) {
        if (e.pointerType !== 'mouse' && e.pointerType !== 'pen') return;
        c.hold = true;
        if (c.collab && canHover) PZ.scramble(c.collab, { accent: 'var(--red)' });
      });
      on(el, 'pointerleave', function (e) {
        if (e.pointerType !== 'mouse' && e.pointerType !== 'pen') return;
        c.hold = false;
        c.nextAt = Math.max(c.nextAt, performance.now() + cycleMs * 0.6);
      });
      /* GL hover handlers are always wired; they act only once the engine is ready (useGLHover) */
      {
        on(media, 'pointerenter', function (e) {
          if (e.pointerType !== 'mouse' && e.pointerType !== 'pen') return;
          c.prime();
          if (!useGLHover || c.plain()) return;
          var p = c.local(e);
          c.m = p; c.m0 = p.slice(); c.blob = p.slice();
          c.inside = true; c.R = BLOB_R; c.revealT = 1; c.active = true; c.still = 0;
          wakeLoop();
        });
        on(media, 'pointermove', function (e) {
          if (e.pointerType !== 'mouse' && e.pointerType !== 'pen') return;
          if (!useGLHover || c.plain()) return;
          var p = c.local(e);
          if (!c.inside) { c.m0 = p.slice(); c.blob = p.slice(); c.R = BLOB_R; c.still = 0; }
          c.m = p; c.inside = true; c.active = true;
          if (!c.tapped) c.revealT = 1;
        });
      }
      /* leaving always hands the card back to its (playing) media; a video never stays paused */
      on(media, 'pointerleave', function (e) {
        if (e.pointerType !== 'mouse' && e.pointerType !== 'pen') return;
        c.inside = false;
        if (!c.tapped) c.revealT = 0;
        c.syncVideo();
      });
      /* touch: first tap on the image reveals, second tap follows the link */
      on(media, 'click', function (e) {
        var touchy = lastType === 'touch' || (!canHover && lastType !== 'mouse');
        lastType = 'mouse';
        if (!touchy) return;
        if (c.plain()) return;
        if (c.tapped || el.classList.contains('is-tapped')) return;
        e.preventDefault();
        closeTapped(el);
        el.classList.add('is-tapped');
        c.tapped = true;
        if (useGLHover) {
          var p = c.local(e);
          if (!(p[0] >= 0 && p[0] <= 1 && p[1] >= 0 && p[1] <= 1)) p = [0.5, 0.5];
          c.tap = p; c.m = p.slice(); c.m0 = p.slice(); c.blob = p.slice();
          c.R = TAP_R; c.revealT = 1; c.burst = 1; c.active = true; c.still = 0;
          wakeLoop();
          c.prime();
        }
      });
    });
    on(d, 'pointerdown', function (e) {
      if (!e.target.closest || !e.target.closest('[data-pzpx-card]')) closeTapped(null);
    });

    /* ---- R4: no pinning ----
       The section scrolls like any other: no sticky stage, no extra track height, no scroll-scrubbed fan-out
       (that locked the section for 560px). Cards and decks sit in place from the start; each collaborator name
       fades in once as its card enters. Deck cycling (above) is unchanged. */
    var mm = null;
    if (!reduced && 'IntersectionObserver' in w && slots.length) {
      root.classList.add('is-fade');
      slots.forEach(function (slot) {
        offs.push(PZ.inView(slot, function (inside) { if (inside) slot.classList.add('is-in'); }, { once: true, threshold: 0.2, media: false }));
      });
      offs.push(function () { root.classList.remove('is-fade'); slots.forEach(function (s) { s.classList.remove('is-in'); }); });
    }

    /* ---- R10/R11: pinned headline + the cards float up past it (parallax) ----
       .pzpx__pin (kicker + headline) is CSS-sticky 10px from the top for the whole card field. While it is stuck it
       gets .is-stuck: a dark backdrop fades in behind it (cards slide under the headline instead of colliding with its
       letters) and, while the site header is shown, it docks under the header instead of being covered by it.
       R11: from the moment it locks it also gets .is-small: on desktop (CSS) the headline scales down to the size of
       the GLASSWARE title (read from that section, so it follows its fit) and the backdrop shrinks with it, freeing the
       screen for the cards. It stays small until the field top comes back below the lock line (no regrow at the end).
       R11 parallax: each card is offset by (its centre - the screen centre) * F: a card sits at its resting place as it
       crosses the middle of the screen and otherwise moves at (1 + F)x the page speed, so neighbours visibly rise at
       different speeds. No card is pushed below the lowest label (it would run into the cue and Drop Log), and the last
       row eases toward page speed so no gap opens above the cue. Work happens on scroll frames only, and only while the
       section is on screen (the scroll listener is removed off screen). Reduced motion: plain scroll (no offsets; the
       pin still sticks).
       R13 (a little more noticeable): desktop F 0.02-0.45 per card (was 0.04-0.24), so cards rise at 1.02x-1.45x. Phones
       (two columns): the left column rises faster (F 0.28-0.40) than the right (0.04-0.14), each card a little
       different, 1.01x-1.40x (was 1.01x-1.17x). Cards in the same column never cover each other's photo or label on
       screen (tightest on-screen gap 72px desktop, 10px phones), values picked with a simulation of this code on the
       live layout (C:\dev\_tools\pz-r13\sim-px.js). */
    var pin = root.querySelector('.pzpx__pin');
    if (field) {
      var F = [0.24, 0.4, 0.06, 0.45, 0.15, 0.32, 0.02, 0.36, 0.1, 0.28, 0.2, 0.4];
      var FL = [0.34, 0.28, 0.4, 0.3, 0.38, 0.32], FR = [0.08, 0.14, 0.04, 0.1, 0.06, 0.12]; // phones: left / right column
      var pxRaf = 0, pxOn = false, pxPhone = w.matchMedia('(max-width: 749px)'), stuck = false, small = false, fresh = true;
      var geo = [], low = 0, pinTop0 = 0, pinH = 0, lastY = [];
      var glassT = d.querySelector('.pz-sc[data-cat="glass"] .pz-sc__title > span') || d.querySelector('.pz-sc__title > span');
      /* --pin-s: GLASSWARE title size / COLLABS title size; --pin-free: the headline height that scale gives back */
      var pinFit = function () {
        if (!pin || !title) return;
        var fs = parseFloat(getComputedStyle(title).fontSize) || 0;
        var to = glassT ? parseFloat(getComputedStyle(glassT).fontSize) || 0 : 0;
        if (!to) to = Math.max(48, Math.min(96, w.innerWidth * 0.045));
        var sc = fs ? Math.min(1, to / fs) : 1;
        pin.style.setProperty('--pin-s', sc.toFixed(4));
        pin.style.setProperty('--pin-free', Math.round(title.offsetHeight * (1 - sc)) + 'px');
      };
      /* untransformed card boxes (offsetTop/offsetHeight ignore the parallax transform) and the label overhang: the
         field ends at the lowest photo, --lab = how far the lowest name + price label reaches past it */
      var measureGeo = function () {
        if (dead) return;
        var fh = field.offsetHeight;
        low = fh;
        var fw = field.offsetWidth, li = 0, ri = 0;
        geo = slots.map(function (s, i) {
          var t = s.offsetTop, h = s.offsetHeight; if (t + h > low) low = t + h;
          var left = s.offsetLeft + s.offsetWidth / 2 < fw / 2;
          return { t: t, h: h, f: F[i % F.length], fp: left ? FL[li++ % FL.length] : FR[ri++ % FR.length] };
        });
        root.style.setProperty('--lab', Math.max(0, Math.ceil(low - fh)) + 'px');
        if (pin) { pinTop0 = parseFloat(getComputedStyle(pin).marginTop) || 0; pinH = pin.offsetHeight; }
        pinFit();
      };
      measureGeo();
      if (d.fonts && d.fonts.ready) d.fonts.ready.then(function () { measureGeo(); if (pxOn) pxScroll(); });
      var mg;
      on(w, 'resize', function () { clearTimeout(mg); mg = setTimeout(function () { measureGeo(); if (pxOn) pxScroll(); }, 160); });
      var pxApply = function () {
        pxRaf = 0;
        if (dead) return;
        var r = field.getBoundingClientRect(), vh = w.innerHeight;
        if (pin) {
          var locked = r.top + pinTop0 < 10;
          var now = locked && r.bottom - pinH > 10;
          if (now !== stuck) { stuck = now; pin.classList.toggle('is-stuck', now); }
          if (locked !== small) {
            small = locked;
            if (small) pinFit();
            /* entering the section already past the lock line (e.g. scrolling back up from Drop Log): no shrink animation */
            if (fresh && title) { title.style.transition = 'none'; pin.classList.toggle('is-small', small); void title.offsetWidth; title.style.transition = ''; }
            else pin.classList.toggle('is-small', small);
            fillShrunk(!fresh);
          }
        }
        fresh = false;
        if (reduced) return;
        var c = vh / 2, ph = pxPhone.matches;
        for (var i = 0; i < slots.length; i++) {
          var g = geo[i];
          if (!g) continue;
          /* cards whose label ends near the bottom of the field ease toward page speed (x0.2 for the last one) so the
             gap above the cue stays tight while the section leaves */
          var room = low - (g.t + g.h);
          var y = (r.top + g.t + g.h / 2 - c) * (ph ? g.fp : g.f) * Math.min(1, Math.max(0.2, room / (vh * 0.4)));
          if (y > room) y = room;
          y = Math.round(y * 10) / 10;
          if (y !== lastY[i]) { lastY[i] = y; slots[i].style.transform = 'translate3d(0,' + y + 'px,0)'; }
        }
      };
      var pxScroll = function () { if (!pxRaf) pxRaf = requestAnimationFrame(pxApply); };
      var pxOff = function () {
        w.removeEventListener('scroll', pxScroll);
        w.removeEventListener('resize', pxScroll);
        cancelAnimationFrame(pxRaf); pxRaf = 0;
      };
      offs.push(PZ.inView(root, function (inside) {
        if (inside === pxOn) return;
        pxOn = inside;
        if (inside) { fresh = true; w.addEventListener('scroll', pxScroll, { passive: true }); w.addEventListener('resize', pxScroll); pxApply(); }
        else { pxOff(); if (pin && stuck) { stuck = false; pin.classList.remove('is-stuck'); } }
      }, { rootMargin: '120px 0px', media: false }));
      offs.push(function () {
        pxOff(); clearTimeout(mg);
        if (pin) pin.classList.remove('is-stuck', 'is-small');
        slots.forEach(function (s) { s.style.transform = ''; });
        root.style.removeProperty('--lab');
      });

      /* R20 test (section setting title_fill, staging template index.dive.json only): the pinned headline's letters fill
         with beer (assets/pz-fill.js). 0 until the headline locks; on desktop it also waits until the R11 shrink has
         finished (280 ms transition, gate 300 ms). Then linear: 0 at the lock, 1 when the next section (Drop Log) first
         enters the bottom of the screen; scrolling back up drains it. The canvas lives inside the scaled title, so it
         shrinks with it; it is rebuilt at the new scale once the shrink (or regrow) is done so it stays sharp. The ink
         band (R15) and the shrink itself are untouched. */
      var fill = null, smallAt = 0, fillT = 0;
      var fillShrunk = function (anim) {
        smallAt = performance.now() - (anim ? 0 : 1000);
        if (!fill) return;
        clearTimeout(fillT);
        fillT = setTimeout(function () { if (!dead && fill) { fill.rebuild(); fill.wake(); } }, anim ? 320 : 0);
      };
      if (pin && title && root.classList.contains('pzpx--fill') && PZ.titleFill) {
        fill = PZ.titleFill({
          root: root, box: title, text: title, fontUrl: root.getAttribute('data-fill-font'),
          target: function () {
            var r = field.getBoundingClientRect(), lockTop = 10 - pinTop0;
            if (r.top >= lockTop) return 0;
            if (!pxPhone.matches && (!small || performance.now() - smallAt < 300)) return 0;
            var sec = root.closest('.shopify-section') || root, nx = sec.nextElementSibling;
            var end = nx ? nx.getBoundingClientRect().top : sec.getBoundingClientRect().bottom;
            var L = end - r.top - w.innerHeight + lockTop;
            return L > 1 ? (lockTop - r.top) / L : 1;
          }
        });
        if (fill) offs.push(function () { clearTimeout(fillT); fill.destroy(); fill = null; });
      }
    }

    /* R11: the cue under the last collab types itself in once, like the kicker */
    var cue = root.querySelector('.pzpx__next-t');
    if (cue) offs.push(PZ.inView(cue, function (inside) { if (inside) PZ.scramble(cue, { accent: 'var(--red)' }); }, { once: true, threshold: 1 }));

    return function destroy() {
      dead = true;
      stop();
      offs.forEach(function (f) { try { f(); } catch (e) {} });
      if (mm) mm.revert();
      cards.forEach(function (c) { c.destroy(); });
      if (eng && eng.gl) { var ext = eng.gl.getExtension('WEBGL_lose_context'); if (ext) ext.loseContext(); }
      root.classList.remove('is-gl', 'is-live', 'is-sky');
    };
  }

  PZ.onSection('pixel-reveal', init);
})();
